import React, { useEffect, useState } from 'react';
import images from '../../constants/images';
import { useTranslation } from 'react-i18next';
import Header from '../../components/header/Header';
import '../../assets/styles/global.css';
import Map from '../../components/map/Map';
import styles from '../about/About.module.css';
import Footer from '../../components/footer/Footer';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { red } from '@mui/material/colors';
import CursorFollower from '../../components/cursor/Cursor';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import '../spinner.css';
import { useTheme, FormHelperText } from '@mui/material';

const Careers: React.FC = () => {
  const career = [
    {
      image: images.core,
      name: 'career.core',
      para: 'career.corePara',
    },
    {
      image: images.environment,
      name: 'career.environment',
      para: 'career.environmentPara',
    },
    {
      image: images.growth,
      name: 'career.growth',
      para: 'career.growthPara',
    },
  ];
  const { t } = useTranslation('global');
  const [isLoading, setIsLoading] = useState(true);
  const [showButton, setShowButton] = useState(false);
  const [responseMsg, setResponseMsg] = useState('');
  const [responseMsgColor, setResponseMsgColor] = useState('success');
  const theme: any = useTheme();

  useEffect(() => {
    const hasLoadedBefore = sessionStorage.getItem('hasLoadedBefore');

    const timer = setTimeout(
      () => {
        setIsLoading(false);
        sessionStorage.setItem('hasLoadedBefore', 'true'); // mark as loaded
      },
      hasLoadedBefore ? 1000 : 3000,
    ); // shorter delay if already loaded

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowButton(window.scrollY > 410); // Show button after scrolling 200px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleScrollToContact = (event: any) => {
    event.preventDefault(); // Prevent default anchor behavior
    const headerHeight = document.querySelector('header')?.offsetHeight || 0; // Get header height
    const targetPosition = document.getElementById('bottom')?.offsetTop || 0;

    window.scrollTo({
      top: targetPosition - headerHeight, // Subtract the header height
      behavior: 'smooth', // Smooth scrolling effect
    });
  };

  const title = t('career.careerTitle');
  const words = title.split(' ');

  const resetForm = () => {
    setFormData({
      from_name: '',
      email: '',
      message: '',
    });

    setErrors({
      email: '',
    });
  };

  const [formData, setFormData] = useState({
    from_name: '',
    email: '',
    message: '',
  });

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });
  };

  const [errors, setErrors] = useState({
    email: '', // Only email will be validated
  });

  const validate = () => {
    const newErrors = { ...errors };
    let valid = true;

    // Email validation: Check if it's a valid email format
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailPattern.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
      valid = false;
    } else {
      newErrors.email = '';
    }

    setErrors(newErrors);
    return valid;
  };

  const sendEmail = (e: any) => {
    e.preventDefault();

    if (validate()) {
      const templateParams = {
        from_name: formData.from_name,
        email: formData.email,
        message: formData.message,
      };
      console.log(process.env.REACT_APP_EMAILJS_SERVICE_ID);

      emailjs
        .send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID ?? '', // Replace with your service ID
          process.env.REACT_APP_EMAILJS_JOB_TEMPLATE_ID ?? '', // Replace with your template ID
          templateParams,
          {
            publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
          },
        )
        .then(
          (response) => {
            console.log('Email sent successfully:', response);
            setResponseMsgColor('success');
            setResponseMsg('Thank you! Your application has been submitted.');
            setTimeout(() => {
              setResponseMsg('');
            }, 3000);
            resetForm(); // <-- Clear the form here
          },
          (error) => {
            console.log('Email sending error:', error);
            setResponseMsgColor('error');
            setResponseMsg(
              'Oops! Something went wrong. Please try again later.',
            );
            setTimeout(() => {
              setResponseMsg('');
            }, 2000);
          },
        );
    } else {
      console.log('Validation failed!');
    }
  };

  if (isLoading) {
    return (
      <>
        <div className="loader-wrapper">
          <div className="spinner">
            <div className="cube1"></div>
            <div className="cube2"></div>
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      <CursorFollower />

      {/* Hero Section */}
      <section
        id="hero"
        className="relative top-0 left-0 z-50 font flex flex-col justify-center items-center min-h-screen"
      >
        {/* Header */}
        <Header />

        {/* Background Image */}
        <img
          src={images.careers}
          alt="Careers Background"
          className="absolute inset-0 object-cover w-screen h-full"
        />

        {/* Overlay */}
        <div className="absolute inset-0"></div>

        {/* Content on top of the image */}
        <div
          className={`relative z-10 flex flex-col justify-between items-center sm:text-center text-left text-white px-4 md:px-48 lg:px-52 xl:px-64`}
        >
          {/* Responsive Heading */}
          <h1
            className={`title md:text-center text-4xl md:text-4xl lg:text-5xl xl:text-5xl 2xl:text-6xl font-bold pb-4 ${styles.head}`}
          >
            {words.map((word, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, y: 50 }} // Start with invisible and slightly below
                animate={{ opacity: 1, y: 0 }} // Animate to fully visible and centered
                transition={{
                  delay: index * 0.3, // Delay each word animation
                  type: 'spring',
                  stiffness: 100,
                  damping: 25,
                }}
                className="inline-block mr-2" // Add some space between words
              >
                {word}
              </motion.span>
            ))}
          </h1>
        </div>

        {/* Scroll Button: Visible only on screens larger than 'lg' */}
        <div className="hidden lg:flex absolute lg:bottom-0 xl:bottom-0 flex-col items-center">
          <a
            href="#bottom"
            onClick={handleScrollToContact}
            className="flex items-center justify-center color text-white  lg:w-[60px] lg:h-[60px] xl:w-[75px] xl:h-[75px] 2xl:w-[105px] 2xl:h-[105px]  hover:bg-red-500 transition duration-300"
          >
            {/* Icon and Text Container */}
            <div className="flex flex-col items-center">
              {/* Arrow Icon */}
              <img
                src={images.down}
                alt="Down Arrow"
                className="w-4 md:w-5 lg:w-5 xl:w-6 2xl:w-7 h-auto mb-1"
              />
              {/* Scroll Text */}
              <span className="text-xs md:text-sm lg:text-sm xl:text-base 2xl:text-lg">
                {t('heroSection.scroll')}
              </span>
            </div>
          </a>
        </div>

        {/* WhatsApp Floating Button */}
        <a
          href="https://wa.me/919909998890"
          target="_blank"
          rel="noopener noreferrer"
          className={`fixed bottom-10 right-5 sm:right-4 md:right-4 lg:right-8 xl:right-10 bg-green-500 text-white rounded-full p-2 lg:p-3 shadow-lg hover:bg-green-600 transition z-50 ${styles.whatsappBackground}`}
        >
          {/* WhatsApp Image Icon */}
          <img
            src={images.whatsapp_white}
            alt="WhatsApp Icon"
            className={`w-6 lg:w-7 h-auto ${styles.whatsappIcon}`}
          />
        </a>
      </section>

      {/* Why work with us? */}
      <section
        id="bottom"
        className="relative font py-8 px-8 sm:py-12 sm:px-12 lg:py-16 md:px-16"
      >
        <h1 className="title font-bold text-3xl sm:text-4xl 2xl:text-5xl text-center pb-6">
          {t('career.work')}
        </h1>
        <div
          className={`flex flex-col lg:flex-row justify-between items-center  gap-x-6 md:gap-x-4 lg:gap-x-4 2xl:gap-x-10`}
        >
          {career.map((career, index) => (
            <div
              key={index}
              className="flex flex-col text-center justify-center items-center bg-white "
            >
              <img
                src={career.image}
                alt={t(career.name)}
                className={`md:w-16 lg:w-12 2xl:w-16 h-auto pb-4 object-cover ${styles.team}`}
              />
              <h3
                className={`title pb-4 font-semibold text-xl md:text-2xl lg:text-xl 2xl:text-2xl ${styles.name}`}
              >
                {t(career.name)}
              </h3>
              <p
                className="font font-normal text-base md:text-xl lg:text-base 2xl:text-xl lg:pb-0 pb-5"
                style={{
                  fontFamily: "'Nunito Sans', sans-serif",
                  fontWeight: 400,
                }}
              >
                {t(career.para)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Join Our Team */}
      <section className="relative flex flex-col md:px-0 px-6 py-4 lg:py-8 2xl:py-12 back-color">
        <h2 className="title text-4xl 2xl:text-5xl text-left md:text-center font-bold pt-8 text-gray-800">
          {t('career.team')}
        </h2>
        <img
          src={images.rectangle}
          alt={'Rectangle'}
          className="h-12 md:h-14 absolute left-4 top-28 md:left-14 lg:left-12 md:top-36 lg:top-40 2xl:top-44 bg-cover"
        />
        <form
          onSubmit={sendEmail}
          className="relative justify-center space-y-4 p-0 pt-10 md:pt-0 pb-8 md:pb-0 md:p-16 lg:p-16"
        >
          <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 space-x-0 md:space-x-4">
            <input
              type="text"
              name="from_name"
              value={formData.from_name}
              onChange={handleInputChange}
              placeholder="Name*"
              className="border p-3 w-full 2xl:h-16 md:w-1/2 focus:outline-none focus:ring-1 focus:ring-[#797979]"
              required
            />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email*"
              className={`border p-3 w-full md:w-1/2 focus:outline-none ${errors.email ? 'border-red-500' : 'focus:ring-1 focus:ring-[#797979]'}`}
              required
            />
          </div>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            placeholder="Message*"
            className="border p-3 w-full focus:outline-none focus:ring-1 focus:ring-[#797979] h-56 md:h-44 2xl:h-60 resize-none"
            required
          ></textarea>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    color: red[500],
                    '&.Mui-checked': {
                      color: red[600],
                    },
                  }}
                />
              }
              label={t('career.agree')}
              className="font font-normal text-sm 2xl:text-lg text-gray-500"
            />
          </FormGroup>
          <button
            type="submit"
            className="w-32 2xl:w-36 color text-white py-3 2xl:py-4 2xl:text-base font-semibold hover:bg-red-600 transition duration-300"
          >
            {t('career.apply')}
          </button>
          {responseMsg && (
            <FormHelperText
              sx={{
                color:
                  responseMsgColor === 'success'
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                fontSize: '16px',
                fontFamily: "'Nunito Sans', sans-serif",
              }}
            >
              {responseMsg}
            </FormHelperText>
          )}

          {/* Scroll to Top Button */}
          {showButton && (
            <div className="fixed xl:top-1/3 lg:top-2/4 md:top-1/4 top-1/3 right-0 transform z-50 ">
              <div className="py-8 px-3 md:py-6 md:px-6 lg:py-7 xl:py-10 xl:px-16 "></div>
              <div
                className="absolute flex flex-col lg:-right-4 xl:right-0 translate-y-1/2 hidden lg:flex  "
                onClick={scrollToTop}
                style={{ cursor: 'pointer' }}
              >
                <img
                  src={images.up}
                  alt="UpArrow"
                  className={`flex justify-center xl:pb-8 xl:h-16 lg:h-12 lg:pb-6 ${styles.icon}`}
                />
                <button
                  onClick={scrollToTop}
                  className={`flex text-sm md:text-lg lg:text-sm xl:text-base 2xl:text-lg text-red-500 rotate-90 transition duration-300 ${styles.top}`}
                >
                  {t('whoWeAre.top')}
                </button>
              </div>
            </div>
          )}
        </form>
      </section>

      {/* Map Section */}
      <Map />

      {/* Footer Section */}
      <Footer />
    </>
  );
};

export default Careers;
